var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { NON_VISUAL_LANGUAGES, SUPPORTED_LANGUAGES, } from '@showroomjs/core';
import { styled } from '@showroomjs/ui';
import cx from 'classnames';
import parseNumRange from 'parse-numeric-range';
import * as React from 'react';
import { useCodeTheme } from '../lib/code-theme-context';
import { useCodeCompilationCache } from '../lib/use-code-compilation';
import { Div } from './base';
import { CodeHighlight } from './code-highlight';
import { CodeLiveEditor, NonVisualCodeLiveEditor, } from './code-live-editor';
import { CodePlayground } from './code-playground';
import { A11yResultContextProvider } from '../lib/use-a11y-result';
import { LanguageTag } from './language-tag';
const IsBlockCodeContext = React.createContext(false);
IsBlockCodeContext.displayName = 'ShowroomIsBlockCodeContext';
export const Pre = (props) => (React.createElement(Div, { css: {
        marginTop: '$4',
        marginBottom: '$8',
    } },
    React.createElement(IsBlockCodeContext.Provider, { value: true }, props.children)));
export const Code = (_a) => {
    var { static: isStatic, inlineBlock, fileName, noEditor, frame, initialHeight, initialWidth, height, highlights } = _a, props = __rest(_a, ["static", "inlineBlock", "fileName", "noEditor", "frame", "initialHeight", "initialWidth", "height", "highlights"]);
    const isBlockCode = React.useContext(IsBlockCodeContext);
    const theme = useCodeTheme();
    const highlightedLines = React.useMemo(() => highlights
        ? parseNumRange(highlights.replace(/^\{|\}$/g, ''))
        : undefined, []);
    if (!isBlockCode || typeof props.children !== 'string') {
        return React.createElement(InlineCode, Object.assign({}, props));
    }
    const lang = props.className && props.className.split('-').pop();
    const code = props.children.trim();
    const heading = fileName ? (React.createElement(Div, { css: {
            px: '$4',
            py: '$3',
            backgroundColor: '$gray-200',
            roundedT: '$md',
            fontSize: '$sm',
            lineHeight: '$sm',
        } },
        React.createElement("code", null, fileName))) : null;
    if (!SUPPORTED_LANGUAGES.includes(lang) || isStatic) {
        return (React.createElement(React.Fragment, null,
            heading,
            React.createElement(Div, { style: Object.assign({}, (typeof theme.plain === 'object' ? theme.plain : {})), css: {
                    py: 10,
                    fontSize: 14,
                    roundedT: heading ? '$none' : inlineBlock ? '$xl' : '$base',
                    roundedB: inlineBlock ? '$xl' : '$base',
                    whiteSpace: 'pre',
                    fontFamily: 'monospace',
                    position: 'relative',
                    display: inlineBlock ? 'inline-block' : 'block',
                    px: inlineBlock ? '$6' : 10,
                    overflow: 'auto',
                }, className: cx('react-showroom-code', props.className) },
                React.createElement(CodeHighlight, { code: code, language: lang, theme: theme, highlights: highlightedLines }),
                !inlineBlock && lang && React.createElement(LanguageTag, { language: lang }))));
    }
    return (React.createElement(React.Fragment, null,
        heading,
        NON_VISUAL_LANGUAGES.includes(lang) ? (React.createElement(NonVisualCodeLiveEditor, { code: code, lang: lang, id: props.id, noEditor: noEditor, frame: frame })) : (React.createElement(A11yResultContextProvider, null,
            React.createElement(VisualCodeBlock, { code: code, lang: lang, id: props.id, hasHeading: !!heading, noEditor: noEditor, frame: frame, initialHeight: initialHeight, initialWidth: initialWidth, height: height })))));
};
const VisualCodeBlock = (props) => {
    const compileCache = useCodeCompilationCache(props.code, props.lang);
    const propsEditorFeature = React.useMemo(() => compileCache.data &&
        compileCache.data.type === 'success' &&
        compileCache.data.features.find((f) => f.feature === 'propsEditor'), [compileCache.data]);
    const hasUnionProps = React.useMemo(() => compileCache.data &&
        compileCache.data.type === 'success' &&
        compileCache.data.features.some((f) => f.feature === 'unionProps'), [compileCache.data]);
    if (propsEditorFeature) {
        return (React.createElement(CodePlayground, Object.assign({}, props, { hasPropsEditor: propsEditorFeature.hasRenderEditor })));
    }
    return (React.createElement(CodeLiveEditor, Object.assign({}, props, (hasUnionProps
        ? {
            noEditor: true,
        }
        : {}))));
};
const InlineCode = styled('code', {
    fontFamily: '$mono',
    backgroundColor: '$gray-200',
    display: 'inline-block',
    px: '$1',
    borderRadius: '$sm',
});
//# sourceMappingURL=code-block.js.map